import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { UpsertOrganizationBusinessIdentityInput } from '@releve/shared';
import { authedPut } from '@/lib/api';
import { toast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

export interface OrganizationBusinessIdentity {
  id: string;
  organization_id: string;
  registration_number: string | null;
  tax_entity_number: string | null;
  incorporated_on: string | null;
  legal_form_id: string | null;
  website_url: string | null;
  contact_email: string | null;
  registered_address: string | null;
  postal_address: string | null;
  iban: string | null;
  bic: string | null;
  is_tax_exempt: boolean;
  receives_public_subsidy: boolean;
  reporting_standard: string | null;
  board_remuneration_policy: string | null;
  sector: string | null;
  created_at: string;
  updated_at: string;
  legal_forms: {
    id: string;
    code: string;
    name: string;
    country_code: string;
  } | null;
}

export function useOrganizationBusinessIdentity(organizationId?: string) {
  return useQuery({
    queryKey: ['organization-business-identity', organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from('organization_business_identity')
        .select(`
          *,
          legal_forms (
            id,
            code,
            name,
            country_code
          )
        `)
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;
      return data as OrganizationBusinessIdentity | null;
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
}

/** The upsert body — the backend's strict schema (no organization_id). */
export type UpsertBusinessIdentityData = UpsertOrganizationBusinessIdentityInput;

export function useUpsertOrganizationBusinessIdentity() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      organizationId,
      data,
    }: {
      organizationId: string;
      data: UpsertBusinessIdentityData;
    }) => {
      // PUT /api/v1/organization-identity/orgs/:orgId — the backend upserts
      // the org's single row (org-member RLS + backend token).
      await authedPut<unknown>(`/api/v1/organization-identity/orgs/${organizationId}`, data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['organization-business-identity', variables.organizationId],
      });
      toast.success(t('organizations.settings.identity.saved'));
    },
    onError: () => {
      toast.error(t('organizations.settings.identity.saveFailed'));
    },
  });
}
